// DESCRIPTION: Pagination component for event lists — page number buttons, prev/next, items-per-page selector.
// DESCRIPTION: Uses design-system tokens only (accent/neutral/semantic). No slate-* or hardcoded colors.

import { __ } from '@wordpress/i18n';
import { SelectControl } from '@wordpress/components';

/**
 * Pagination component for event lists
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {number}   props.currentPage          Current page number (1-based).
 * @param {number}   props.totalPages           Total number of pages.
 * @param {number}   props.totalItems           Total number of items.
 * @param {number}   props.itemsPerPage         Items per page.
 * @param {Function} props.onPageChange         Callback for page changes.
 * @param {Function} props.onItemsPerPageChange Callback for page size changes.
 * @param {boolean}  props.showItemsPerPage     Show items per page selector.
 * @param {boolean}  props.showInfo             Show pagination info text.
 * @param {boolean}  props.disabled             Disable all controls.
 * @param {Object}   props.config               Configuration options.
 * @return {JSX.Element} The Pagination component.
 */
const Pagination = ( {
	currentPage = 1,
	totalPages = 1,
	totalItems = 0,
	itemsPerPage = 10,
	onPageChange = () => {},
	onItemsPerPageChange = () => {},
	showItemsPerPage = true,
	showInfo = true,
	disabled = false,
	config = {},
} ) => {
	const defaultConfig = {
		maxVisiblePages: 7,
		pageSizeOptions: [ 10, 25, 50, 100 ],
		strings: {
			info: __(
				'Showing {start}-{end} of {total} items',
				'nemtly-booking'
			),
			itemsPerPage: __( 'Items per page', 'nemtly-booking' ),
			first: __( 'First', 'nemtly-booking' ),
			previous: __( 'Previous', 'nemtly-booking' ),
			next: __( 'Next', 'nemtly-booking' ),
			last: __( 'Last', 'nemtly-booking' ),
			goToPage: __( 'Go to page {page}', 'nemtly-booking' ),
			currentPage: __( 'Page {page}, current page', 'nemtly-booking' ),
		},
	};

	const finalConfig = { ...defaultConfig, ...config };

	const startItem =
		totalItems === 0 ? 0 : ( currentPage - 1 ) * itemsPerPage + 1;
	const endItem = Math.min( currentPage * itemsPerPage, totalItems );

	/**
	 * Generate page numbers with smart truncation.
	 *
	 * @since PBOOKING_SINCE
	 * @return {Array} Array of page numbers and '...' ellipsis markers.
	 */
	const generatePageNumbers = () => {
		const { maxVisiblePages } = finalConfig;
		const pages = [];

		if ( totalPages <= maxVisiblePages ) {
			for ( let i = 1; i <= totalPages; i++ ) {
				pages.push( i );
			}
		} else {
			const halfVisible = Math.floor( maxVisiblePages / 2 );
			let startPage = Math.max( 1, currentPage - halfVisible );
			let endPage = Math.min( totalPages, currentPage + halfVisible );

			if ( currentPage <= halfVisible ) {
				endPage = Math.min( maxVisiblePages, totalPages );
			}
			if ( currentPage > totalPages - halfVisible ) {
				startPage = Math.max( 1, totalPages - maxVisiblePages + 1 );
			}

			if ( startPage > 1 ) {
				pages.push( 1 );
				if ( startPage > 2 ) {
					pages.push( '...' );
				}
			}

			for ( let i = startPage; i <= endPage; i++ ) {
				if ( i !== 1 && i !== totalPages ) {
					pages.push( i );
				}
			}

			if ( endPage < totalPages ) {
				if ( endPage < totalPages - 1 ) {
					pages.push( '...' );
				}
				pages.push( totalPages );
			}
		}

		return pages;
	};

	/**
	 * @since PBOOKING_SINCE
	 * @param {number} page
	 */
	const handlePageChange = ( page ) => {
		if (
			page >= 1 &&
			page <= totalPages &&
			page !== currentPage &&
			! disabled
		) {
			onPageChange( page );
		}
	};

	/**
	 * @since PBOOKING_SINCE
	 * @param {string} value
	 */
	const handleItemsPerPageChange = ( value ) => {
		if ( ! disabled ) {
			onItemsPerPageChange( parseInt( value, 10 ) );
		}
	};

	if ( totalPages <= 1 && ! showInfo && ! showItemsPerPage ) {
		return null;
	}

	const pageNumbers = generatePageNumbers();

	// Shared classes
	const btnBase =
		'nb-inline-flex nb-items-center nb-justify-center nb-h-8 nb-min-w-8 nb-px-2.5 nb-rounded-md nb-border nb-text-sm nb-font-medium nb-transition-colors nb-duration-fast disabled:nb-opacity-40 disabled:nb-cursor-not-allowed focus-visible:nb-outline-none focus-visible:nb-ring-2 focus-visible:nb-ring-accent-500 focus-visible:nb-ring-offset-1';
	const btnDefault = `${ btnBase } nb-border-neutral-200 nb-bg-neutral-0 nb-text-neutral-700 hover:nb-bg-neutral-50 hover:nb-border-neutral-300`;
	const btnActive = `${ btnBase } nb-border-accent-500 nb-bg-accent-500 nb-text-neutral-0 nb-cursor-default`;

	return (
		<div className="nb-flex nb-flex-wrap nb-items-center nb-justify-between nb-gap-3">
			{ /* Left: info + per-page */ }
			<div className="nb-flex nb-items-center nb-gap-4">
				{ showInfo && (
					<p className="nb-text-sm nb-text-neutral-500">
						{ finalConfig.strings.info
							.replace( '{start}', startItem )
							.replace( '{end}', endItem )
							.replace( '{total}', totalItems ) }
					</p>
				) }

				{ showItemsPerPage && (
					<div className="nb-flex nb-items-center nb-gap-2">
						<label
							htmlFor="nb-items-per-page"
							className="nb-text-sm nb-text-neutral-500 nb-whitespace-nowrap"
						>
							{ finalConfig.strings.itemsPerPage }:
						</label>
						<SelectControl
							id="nb-items-per-page"
							value={ itemsPerPage }
							options={ finalConfig.pageSizeOptions.map(
								( size ) => ( {
									label: size.toString(),
									value: size,
								} )
							) }
							onChange={ handleItemsPerPageChange }
							disabled={ disabled }
							aria-label={ finalConfig.strings.itemsPerPage }
							__next40pxDefaultSize
							__nextHasNoMarginBottom
						/>
					</div>
				) }
			</div>

			{ /* Right: page nav */ }
			{ totalPages > 1 && (
				<nav
					className="nb-flex nb-items-center nb-gap-1"
					aria-label={ __( 'Events pagination', 'nemtly-booking' ) }
					role="navigation"
				>
					{ /* Previous */ }
					<button
						type="button"
						className={ btnDefault }
						onClick={ () => handlePageChange( currentPage - 1 ) }
						disabled={ disabled || currentPage === 1 }
						aria-label={ finalConfig.strings.previous }
					>
						<svg
							className="nb-w-4 nb-h-4"
							fill="none"
							viewBox="0 0 16 16"
						>
							<path
								d="M10 12L6 8l4-4"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>

					{ /* Page numbers */ }
					{ pageNumbers.map( ( page, index ) => {
						if ( page === '...' ) {
							return (
								<span
									key={ `ellipsis-${ index }` }
									className="nb-inline-flex nb-items-center nb-justify-center nb-h-8 nb-w-8 nb-text-sm nb-text-neutral-400"
								>
									…
								</span>
							);
						}

						const isActive = page === currentPage;
						return (
							<button
								key={ page }
								type="button"
								className={ isActive ? btnActive : btnDefault }
								onClick={ () => handlePageChange( page ) }
								disabled={ disabled || isActive }
								aria-label={
									isActive
										? finalConfig.strings.currentPage.replace(
												'{page}',
												page
										  )
										: finalConfig.strings.goToPage.replace(
												'{page}',
												page
										  )
								}
								aria-current={ isActive ? 'page' : undefined }
							>
								{ page }
							</button>
						);
					} ) }

					{ /* Next */ }
					<button
						type="button"
						className={ btnDefault }
						onClick={ () => handlePageChange( currentPage + 1 ) }
						disabled={ disabled || currentPage === totalPages }
						aria-label={ finalConfig.strings.next }
					>
						<svg
							className="nb-w-4 nb-h-4"
							fill="none"
							viewBox="0 0 16 16"
						>
							<path
								d="M6 12l4-4-4-4"
								stroke="currentColor"
								strokeWidth="1.5"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
				</nav>
			) }
		</div>
	);
};

export default Pagination;
